import PauseOutlinedIcon from "@mui/icons-material/PauseOutlined"
import PlayArrowOutlinedIcon from "@mui/icons-material/PlayArrowOutlined"
import CircularProgress from "@mui/material/CircularProgress"
import IconButton from "@mui/material/IconButton"
import Typography from "@mui/material/Typography"
import axios from "axios"
import { useEffect, useRef, useState } from "react"

import { useAuthorizedAxiosClient } from "../../../common/axios"
import Row from "../../../common/components/Row"

const PREVIEW_URL =
  "/crm/api/twilio/concierge-calling-numbers/greeting/preview/"

async function fetchAudioBlob(axiosClient, url) {
  const { data } = await axiosClient.get(url, { responseType: "blob" })
  return data
}

/**
 * Play/pause preview for saved greetings (authenticated blob fetch) or local blob URLs.
 */
export default function GreetingAudioPlayer({
  blobUrl = null,
  reloadKey = null,
  fallbackPlayUrl = null,
}) {
  const axiosAuth = useAuthorizedAxiosClient()
  const audioRef = useRef(null)
  const objectUrlRef = useRef(null)
  const [audioSrc, setAudioSrc] = useState(blobUrl || null)
  const [isLoading, setIsLoading] = useState(!blobUrl && !!reloadKey)
  const [isPlaying, setIsPlaying] = useState(false)
  const [error, setError] = useState(null)

  useEffect(() => {
    setIsPlaying(false)
    setError(null)

    if (blobUrl) {
      setAudioSrc(blobUrl)
      setIsLoading(false)
      return undefined
    }

    if (!reloadKey) {
      setAudioSrc(null)
      setIsLoading(false)
      return undefined
    }

    let cancelled = false
    setIsLoading(true)
    setAudioSrc(null)

    async function loadSavedGreeting() {
      try {
        let blob = null
        try {
          blob = await fetchAudioBlob(axiosAuth, PREVIEW_URL)
        } catch (previewError) {
          if (!fallbackPlayUrl) {
            throw previewError
          }
          // Dev may not have /preview/ yet — fall back to public Twilio play URL.
          blob = await fetchAudioBlob(axios, fallbackPlayUrl)
        }
        if (cancelled) {
          return
        }
        if (objectUrlRef.current) {
          URL.revokeObjectURL(objectUrlRef.current)
        }
        const objectUrl = URL.createObjectURL(blob)
        objectUrlRef.current = objectUrl
        setAudioSrc(objectUrl)
      } catch (_) {
        if (!cancelled) {
          setError("Could not load greeting audio for preview.")
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false)
        }
      }
    }

    loadSavedGreeting()

    return () => {
      cancelled = true
    }
  }, [axiosAuth, blobUrl, reloadKey, fallbackPlayUrl])

  useEffect(
    () => () => {
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current)
        objectUrlRef.current = null
      }
    },
    []
  )

  async function handleTogglePlayback() {
    const audio = audioRef.current
    if (!audio || !audioSrc) {
      return
    }
    if (isPlaying) {
      audio.pause()
      return
    }
    try {
      await audio.play()
    } catch (_) {
      setError("Playback failed. Click play to try again.")
      setIsPlaying(false)
    }
  }

  if (isLoading) {
    return (
      <Row sx={{ mt: 1, alignItems: "center", gap: 1 }}>
        <CircularProgress size={18} />
        <Typography variant="body2" color="text.secondary">
          Loading audio…
        </Typography>
      </Row>
    )
  }

  if (error) {
    return (
      <Typography variant="body2" color="error" sx={{ mt: 1 }}>
        {error}
      </Typography>
    )
  }

  if (!audioSrc) {
    return null
  }

  return (
    <Row sx={{ mt: 1, alignItems: "center", gap: 1, pointerEvents: "auto" }}>
      <IconButton
        aria-label={isPlaying ? "Pause greeting" : "Play greeting"}
        onClick={handleTogglePlayback}
        size="small"
        sx={(theme) => ({
          backgroundColor: theme.palette.primary.main,
          color: "white",
          "&:hover": {
            backgroundColor: theme.palette.primary.dark,
          },
        })}
      >
        {isPlaying ? (
          <PauseOutlinedIcon fontSize="small" />
        ) : (
          <PlayArrowOutlinedIcon fontSize="small" />
        )}
      </IconButton>
      <Typography variant="body2" color="text.secondary">
        {isPlaying ? "Playing…" : "Preview greeting"}
      </Typography>
      <audio
        ref={audioRef}
        src={audioSrc}
        preload="metadata"
        onPlay={() => setIsPlaying(true)}
        onPause={() => setIsPlaying(false)}
        onEnded={() => setIsPlaying(false)}
        style={{ display: "none" }}
      />
    </Row>
  )
}
